import PropTypes from "prop-types";
import React, { useContext, useEffect, useState } from "react";

import {
  AppFooter,
  ApplicationContext,
  Maintenance,
  NoServerSideRendering,
  Page,
  SuppressForIframe,
  SuppressForNative,
} from "@swa-ui/application";
import { AuthResponseDialog } from "@swa-ui/authentication";
import { getBootstrapData } from "@swa-ui/bootstrap";
import { useDeviceInfo } from "@swa-ui/browser";
import { AnimationProvider } from "@swa-ui/core";
import { CustomerTheme } from "@swa-ui/design-tokens";
import { LayoutSuspense, useContentLoadingStatus, usePlacement } from "@swa-ui/placements";
import { classNames } from "@swa-ui/string";

import { DesktopHeaderControl } from "../DesktopHeaderControl";
import { DotcomFooter } from "../DotcomFooter";
import { DotcomHeader } from "../DotcomHeader";
import { DotcomMessages } from "../DotcomMessages";
import { DotcomSystemMessages } from "../DotcomSystemMessages";
import { MobileHeaderControl } from "../MobileHeaderControl";
import { StartupMessage } from "../StartupMessage";
import styles from "./DotcomPageLayout.module.scss";

const TOGGLE_REFLOW_ANIMATIONS_DELAY = 600;

export const DotcomPageLayout = (props) => {
  const {
    appHeader,
    children,
    displayCreateAccountLink = true,
    headerControl,
    HeaderTheme,
    light,
    menuItemSeparator,
    messagesLocation,
    PageTheme,
    pageTitle,
    showDotcomMessages,
    showLoginBanner,
    uppercaseMenuItems,
    waitForContent = true,
  } = props;

  const { basePath, experienceId, footer, header, maintenanceCode, site } =
    useContext(ApplicationContext);
  const { isLargeOrXlarge } = useDeviceInfo();
  const { placement: globalNavHeader } = usePlacement("globalNavHeader");
  const { contentHasLoaded } = useContentLoadingStatus();
  const [enableReflowAnimations, setEnableReflowAnimations] = useState(contentHasLoaded);

  useEffect(() => {
    const enableReflowAnimationsTimeoutId = setTimeout(() => {
      setEnableReflowAnimations(contentHasLoaded);
    }, TOGGLE_REFLOW_ANIMATIONS_DELAY);

    return () => {
      clearTimeout(enableReflowAnimationsTimeoutId);
    };
  }, [contentHasLoaded]);

  return (
    <>
      {messagesLocation === "above" && renderMessages()}
      <PageTheme>
        <Page {...getPageProps()}>
          <div className={styles.pageContent}>
            <Maintenance>
              <SuppressForIframe>
                <AuthResponseDialog />
                {messagesLocation === "below" && renderMessages()}
                <StartupMessage />
              </SuppressForIframe>
              <AnimationProvider enableReflowAnimations={enableReflowAnimations}>
                {children}
              </AnimationProvider>
            </Maintenance>
          </div>
          <SuppressForIframe>
            <AppFooter />
          </SuppressForIframe>
        </Page>
      </PageTheme>
    </>
  );

  function getPageProps() {
    return {
      basePath,
      className: styles.background,
      experienceId,
      footer: renderFooter(),
      header: renderHeader(),
      light,
      suppressMessageForTrackingCodes: getTrackingCodesToSuppress().map(String),
    };
  }

  function renderHeader() {
    return (
      <HeaderTheme>
        <header {...getHeaderContainerProps()}>
          <NoServerSideRendering>
            <SuppressForNative>
              <SuppressForIframe>{renderHeaderContent()}</SuppressForIframe>
            </SuppressForNative>
          </NoServerSideRendering>
          {appHeader}
        </header>
      </HeaderTheme>
    );
  }

  function renderHeaderContent() {
    return header ? header : <DotcomHeader {...getHeaderProps()} />;
  }

  function renderFooter() {
    return (
      <NoServerSideRendering>
        <SuppressForNative>
          <SuppressForIframe>
            {waitForContent ? (
              <LayoutSuspense {...getLayoutSuspenseProps()}>{renderFooterContent()}</LayoutSuspense>
            ) : (
              renderFooterContent()
            )}
          </SuppressForIframe>
        </SuppressForNative>
      </NoServerSideRendering>
    );
  }

  function renderFooterContent() {
    return footer ? footer : <DotcomFooter {...getFooterProps()} />;
  }

  function renderMessages() {
    return (
      showDotcomMessages && (
        <SuppressForIframe>
          <DotcomSystemMessages />
          <div className={styles.messages}>
            <DotcomMessages />
          </div>
        </SuppressForIframe>
      )
    );
  }

  function getTrackingCodesToSuppress() {
    const {
      errorsTriggeringLogout = [],
      errorsTriggeringMfa = [],
      errorsTriggeringReauthentication,
      errorsTriggeringRelogin,
      errorsTriggeringRetry,
    } = getBootstrapData("api-gateway-errors") ?? {};

    return [
      ...(maintenanceCode ? [maintenanceCode] : []),
      ...errorsTriggeringLogout,
      ...errorsTriggeringMfa,
      ...errorsTriggeringReauthentication,
      ...errorsTriggeringRelogin,
      ...errorsTriggeringRetry,
    ];
  }

  function getHeaderContainerProps() {
    return {
      className: classNames(styles.header, {
        [styles.desktop]: isDesktop(),
        [styles.mobile]: !isDesktop(),
      }),
    };
  }

  function getHeaderProps() {
    const type = getNavigationType();

    return {
      appHeader,
      control:
        headerControl ??
        (type === "desktop" ? (
          <DesktopHeaderControl displayCreateAccountLink={displayCreateAccountLink} light={light} />
        ) : (
          <MobileHeaderControl site={site} />
        )),
      light,
      menuItemSeparator:
        globalNavHeader?.primaryLinksOptions?.menuItemSeparator ?? menuItemSeparator,
      pageTitle,
      showLoginBanner,
      type,
      uppercaseMenuItems:
        globalNavHeader?.primaryLinksOptions?.uppercaseMenuItems ?? uppercaseMenuItems,
      waitForContent,
    };
  }

  function getFooterProps() {
    const type = getNavigationType();

    return {
      type,
      waitForContent,
    };
  }

  function getLayoutSuspenseProps() {
    const type = getNavigationType();

    return {
      animate: true,
      animationToken: type,
    };
  }

  function getNavigationType() {
    const isDesktopSite = site === "southwest" || site === "corporate";
    const isLocalEnv = process.env.NODE_ENV === "development";

    return (isLocalEnv && isLargeOrXlarge) || (!isLocalEnv && isDesktopSite) ? "desktop" : "mobile";
  }

  function isDesktop() {
    return getNavigationType() === "desktop";
  }
};

DotcomPageLayout.displayName = "DotcomPageLayout";
DotcomPageLayout.propTypes = {
  /** Header component passed from SPA. */
  appHeader: PropTypes.node,

  /** Content to be rendered on the page. */
  children: PropTypes.node.isRequired,

  /** Indicates whether to display the Create Account link. */
  displayCreateAccountLink: PropTypes.bool,

  /** Header theme to be displayed, such as customer or vacations. */
  HeaderTheme: PropTypes.func,

  /** Indicates whether the app is using a dark Header background, to be passed to Link component for styling. */
  light: PropTypes.bool,

  /** Adds the value of the prop as a separator between the menu links. */
  menuItemSeparator: PropTypes.string,

  /** Changes the place where the DotcomMessages is rendered
     (above or below the header). Default: "below". */
  messagesLocation: PropTypes.oneOf(["above", "below"]),

  /** Page theme to be displayed, such as customer or vacations. */
  PageTheme: PropTypes.func,

  /** Page heading to appear in the global header. */
  pageTitle: PropTypes.string,

  /** Indicates whether to show the dotcom messages */
  showDotcomMessages: PropTypes.bool,

  /** Indicates whether to show the login banner */
  showLoginBanner: PropTypes.bool,

  /** Transform menu item links into uppercase. Default: true */
  uppercaseMenuItems: PropTypes.bool,
};

DotcomPageLayout.defaultProps = {
  HeaderTheme: CustomerTheme,
  messagesLocation: "below",
  PageTheme: CustomerTheme,
  showDotcomMessages: true,
  showLoginBanner: false,
};
